import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import StockBreakdownModal from "../components/StockBreakdownModal";
import { fetchStores, selectStores } from "../features/stores/storesSlice";
import {
  addProductToPurchaseList,
  clearActionState,
  fetchStockOverview,
  generatePreliminaryList,
  selectStockActionError,
  selectStockActionStatus,
  selectStockOverview,
  selectStockOverviewCount,
  selectStockOverviewError,
  selectStockOverviewStatus,
} from "../features/stockOverview/stockOverviewSlice";

const PAGE_SIZE = 50;

export default function StockOverviewPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [low, setLow] = useState(() => searchParams.get("low") === "1");
  const [ordering, setOrdering] = useState(() => searchParams.get("ordering") || "name");
  const [page, setPage] = useState(() => {
    const n = Number(searchParams.get("page") || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });
  const [storeId, setStoreId] = useState("");
  const [breakdownProduct, setBreakdownProduct] = useState(null);
  const [addModal, setAddModal] = useState(null); // { product, quantity }
  // { title, message, confirmLabel?, onConfirm? } — null когда закрыт
  const [dialog, setDialog] = useState(null);

  const rows = useSelector(selectStockOverview);
  const totalCount = useSelector(selectStockOverviewCount);
  const loadStatus = useSelector(selectStockOverviewStatus);
  const error = useSelector(selectStockOverviewError);
  const stores = useSelector(selectStores);
  const actionStatus = useSelector(selectStockActionStatus);
  const actionError = useSelector(selectStockActionError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);

  useEffect(() => {
    dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    if (low) next.set("low", "1");
    if (ordering && ordering !== "name") next.set("ordering", ordering);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [searchTrimmed, low, ordering, page, setSearchParams]);

  useEffect(() => {
    dispatch(fetchStockOverview({ search: searchTrimmed, page, pageSize: PAGE_SIZE, low, ordering }));
  }, [dispatch, searchTrimmed, page, low, ordering]);

  const toggleSort = (field) => {
    setPage(1);
    setOrdering((prev) => (prev === field ? `-${field}` : field));
  };
  const sortArrow = (field) => {
    if (ordering === field) return " ▲";
    if (ordering === `-${field}`) return " ▼";
    return "";
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  useEffect(() => {
    if (loadStatus === "succeeded" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  const visibleRows = Array.isArray(rows) ? rows : [];

  const runGenerate = async () => {
    dispatch(clearActionState());
    const res = await dispatch(generatePreliminaryList({ storeId }));
    if (generatePreliminaryList.fulfilled.match(res)) {
      const { draftId, itemsAdded } = res.payload;
      setDialog({
        title: "Список сформирован",
        message: `В предварительный список добавлено позиций: ${itemsAdded}.`,
        confirmLabel: "Открыть список",
        onConfirm: () => navigate(`/warehouse/purchase-requests/${draftId}`),
      });
    }
  };

  const handleGenerate = () => {
    if (!storeId) {
      setDialog({ title: "Выберите магазин", message: "Сначала выберите магазин в списке вверху." });
      return;
    }
    setDialog({
      title: "Сформировать список?",
      message: `Все товары с остатком ниже порога в магазине «${selectedStoreName}» будут добавлены в черновик списка покупки.`,
      confirmLabel: "Сформировать",
      onConfirm: runGenerate,
    });
  };

  const handleAddConfirm = async () => {
    if (!storeId) {
      setDialog({ title: "Выберите магазин", message: "Укажите магазин для добавления в список." });
      return;
    }
    const quantity = Math.max(1, Number(addModal.quantity) || 1);
    dispatch(clearActionState());
    const res = await dispatch(
      addProductToPurchaseList({ storeId, productId: addModal.product.id, quantity })
    );
    if (addProductToPurchaseList.fulfilled.match(res)) {
      setAddModal(null);
      setDialog({ title: "Добавлено", message: "Товар добавлен в список покупки." });
    }
  };

  const selectedStoreName = stores.find((s) => String(s.id) === String(storeId))?.name;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Остатки товара</div>
          <div style={styles.subTitle}>
            Суммарный остаток по всем магазинам. Нажмите на строку — разбивка по магазинам.
          </div>
        </div>

        <div style={styles.headerActions}>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: название / штрихкод…"
            style={styles.search}
          />
          <Select
            value={storeId}
            onChange={setStoreId}
            options={[
              { value: "", label: "Магазин (для списка)…" },
              ...stores.map((s) => ({ value: String(s.id), label: s.name })),
            ]}
          />
          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={low}
              onChange={(e) => {
                setLow(e.target.checked);
                setPage(1);
              }}
            />
            Только дефицит
          </label>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={actionStatus === "saving"}
            style={styles.btnPrimary}
            title="Авто-сформировать предварительный список для выбранного магазина"
          >
            Сформировать список
          </button>
        </div>
      </div>

      {actionError && <div style={styles.error}>{actionError}</div>}
      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.thSort} onClick={() => toggleSort("name")} title="Сортировать">
                  Название{sortArrow("name")}
                </th>
                <th style={styles.th}>Штрихкод</th>
                <th
                  style={styles.thSort}
                  onClick={() => toggleSort("total_quantity")}
                  title="Сортировать"
                >
                  Всего по магазинам{sortArrow("total_quantity")}
                </th>
                <th
                  style={styles.thSort}
                  onClick={() => toggleSort("stores_count")}
                  title="Сортировать"
                >
                  Магазинов{sortArrow("stores_count")}
                </th>
                <th style={styles.th}>Порог</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Действие</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={8} style={styles.tdMuted}>
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    style={{ ...styles.tr, ...(r.is_low ? styles.trLow : null) }}
                    onClick={() => setBreakdownProduct(r)}
                    title="Разбивка по магазинам"
                  >
                    <td style={styles.tdMono}>{r.id}</td>
                    <td style={styles.td}>{r.name || "—"}</td>
                    <td style={styles.tdMono}>{r.barcode || "—"}</td>
                    <td style={styles.tdMono}>{r.total_quantity ?? 0}</td>
                    <td style={styles.tdMono}>{r.stores_count ?? 0}</td>
                    <td style={styles.tdMono}>{r.effective_threshold ?? "—"}</td>
                    <td style={styles.td}>
                      {r.is_low ? (
                        <span style={styles.badgeLow}>Дефицит</span>
                      ) : (
                        <span style={styles.badgeOk}>OK</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(clearActionState());
                          setAddModal({ product: r, quantity: r.effective_threshold || 1 });
                        }}
                        style={styles.btnSmall}
                      >
                        + В список
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
            styles={styles}
          />
        )}
      </div>

      {breakdownProduct && (
        <StockBreakdownModal product={breakdownProduct} onClose={() => setBreakdownProduct(null)} />
      )}

      {addModal && (
        <div style={styles.overlay} onClick={() => setAddModal(null)}>
          <div style={styles.smallModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.title}>Добавить в список покупки</div>
            <div style={styles.modalSub}>{addModal.product.name}</div>
            <div style={styles.field}>
              <span style={styles.fieldLabel}>Магазин</span>
              <Select
                value={storeId}
                onChange={setStoreId}
                options={[
                  { value: "", label: "Выберите магазин…" },
                  ...stores.map((s) => ({ value: String(s.id), label: s.name })),
                ]}
              />
            </div>
            <div style={styles.field}>
              <span style={styles.fieldLabel}>Количество</span>
              <input
                type="number"
                min={1}
                value={addModal.quantity}
                onChange={(e) => setAddModal((m) => ({ ...m, quantity: e.target.value }))}
                style={styles.search}
              />
            </div>
            {actionError && <div style={styles.error}>{actionError}</div>}
            <div style={styles.modalActions}>
              <button type="button" onClick={() => setAddModal(null)} style={styles.btn}>
                Отмена
              </button>
              <button
                type="button"
                onClick={handleAddConfirm}
                disabled={actionStatus === "saving"}
                style={styles.btnPrimary}
              >
                {selectedStoreName ? `Добавить → ${selectedStoreName}` : "Добавить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {dialog && (
        <div style={styles.overlay} onClick={() => setDialog(null)}>
          <div style={styles.smallModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.title}>{dialog.title}</div>
            {dialog.message && <div style={styles.modalSub}>{dialog.message}</div>}
            <div style={styles.modalActions}>
              {dialog.onConfirm ? (
                <>
                  <button type="button" onClick={() => setDialog(null)} style={styles.btn}>
                    Отмена
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const fn = dialog.onConfirm;
                      setDialog(null);
                      fn();
                    }}
                    style={styles.btnPrimary}
                  >
                    {dialog.confirmLabel || "OK"}
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => setDialog(null)} style={styles.btnPrimary}>
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  header: { padding: "16px 24px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", flexShrink: 0 },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subTitle: { marginTop: 3, fontSize: 15, color: "var(--c-text-muted)", lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  search: { width: 280, maxWidth: "70vw", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  select: { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  checkbox: { display: "flex", alignItems: "center", gap: 6, fontSize: 15, color: "var(--c-text-sub)", cursor: "pointer" },
  btn: { padding: "8px 14px", borderRadius: 99, border: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15 },
  btnPrimary: { padding: "8px 14px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  btnSmall: { padding: "6px 10px", borderRadius: 99, border: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 14 },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  error: { padding: "12px 24px", fontSize: 15, background: "rgba(248,113,113,0.1)", borderTop: "1px solid rgba(248,113,113,0.2)", borderBottom: "1px solid rgba(248,113,113,0.2)", color: "var(--c-danger)" },
  body: { padding: "16px 24px", flex: 1, minHeight: 0, overflow: "auto" },
  tableWrap: { border: "1px solid var(--c-border)", background: "var(--c-card)", borderRadius: 16, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px" },
  thSort: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px", cursor: "pointer", userSelect: "none" },
  tr: { cursor: "pointer" },
  trLow: { background: "rgba(248,113,113,0.07)" },
  td: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", verticalAlign: "middle", color: "var(--c-text)" },
  tdMono: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", color: "var(--c-text)" },
  tdMuted: { padding: 14, fontSize: 15, color: "var(--c-text-muted)" },
  badgeLow: { display: "inline-block", padding: "3px 8px", borderRadius: 99, fontSize: 13, fontWeight: 700, background: "rgba(248,113,113,0.15)", color: "var(--c-danger)" },
  badgeOk: { display: "inline-block", padding: "3px 8px", borderRadius: 99, fontSize: 13, fontWeight: 700, background: "rgba(34,197,94,0.12)", color: "var(--c-success)" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  smallModal: { width: 420, maxWidth: "100%", background: "var(--c-card)", color: "var(--c-text)", border: "1px solid var(--c-border)", borderRadius: 16, padding: 20, boxShadow: "0 20px 50px rgba(0,0,0,0.5)" },
  modalSub: { marginTop: 4, fontSize: 15, color: "var(--c-text-muted)" },
  field: { marginTop: 14, display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: { fontSize: 14, fontWeight: 700, color: "var(--c-text-sub)" },
  modalActions: { marginTop: 18, display: "flex", gap: 8, justifyContent: "flex-end" },
  paginationWrap: { marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  paginationMeta: { fontSize: 14, color: "var(--c-text-muted)", fontWeight: 600 },
  paginationBtns: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  pageBtn: { padding: "7px 10px", borderRadius: 99, border: "1px solid var(--c-border)", background: "var(--c-card)", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 14, minWidth: 34 },
  pageBtnActive: { border: "1px solid rgba(245,197,24,0.4)", background: "var(--c-accent)", color: "var(--c-accent-on)" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  pageDots: { color: "var(--c-text-muted)", fontWeight: 700, padding: "0 6px" },
};
